import PropTypes from "prop-types";
import React, { useState } from "react";
import { injectIntl, intlShape } from "react-intl";
import { SidebarNavigation } from "@yoast/ui-library";
import * as styles from "./styles.module.scss";
import { NavLink } from "react-router-dom";
import { Logo } from "../Logo";
import UserStatus from "../../containers/UserStatus";
import { UserIcon } from "@heroicons/react/24/outline";
import AiIcon from "../../images/ai_icon.svg";
import YoastPlatformIcon from "../../images/yoast_platform.svg";
import NeedBrandInsightsModal from "../Sidebar/NeedBrandInsights";

/**
 * The AI icon component.
 *
 * @returns {JSX.Element} The AI icon as an image element.
 */
const AiIconJSX = () => {
	return <img src={ AiIcon } alt="AI Icon" />;
};

/**
 * The Yoast Platform icon component.
 *
 * @returns {JSX.Element} The Yoast Platform icon as an image element.
 */
const YoastPlatformIconJSX = () => {
	return <img src={ YoastPlatformIcon } alt="Yoast Platform Icon" />;
};


/**
 *
 * @param {Object} props The props to use.
 *
 * @returns {Element} The rendered menu items.
 */
const MenuItems = props => {
	/**
	 * Handles the click on the Brand Insights menu item.
	 * @param {Event} e The event.
	 *
	 * @returns {void}
	 */
	const pressBrandInsightsMenuItem = () => {
		// NOTE: This is commented to bypass showing the notice as it now appears
		// in the AIBI interface when needed, allowing the user to view existing
		// brands and past analysis information if it exists.
		//
		// In the future, we could potentially ask AIBI API whether any brands
		// exist, and then only if they do not, show the modal here directly.
		// See discussion at https://github.com/Yoast/Platform/pull/3571
		//
		// if ( ! props.hasValidBrandInsightsLicense ) {
		// 	e.preventDefault();
		// 	props.setShowBrandInsightsNotice( true );
		// }
	};

	return <div>
		{
			props.menuRoutes
				.filter( page => page.showInMenu )
				.map( page => {
					const title = props.intl.formatMessage( page.title );
					const label = (
						<span className={ styles.menuItem }>
							{ page.iconSource }
							{ title }
							{ page.isExternal && page.externalLinkIconSource }
						</span>
					);

					return page.isExternal ? (
						<SidebarNavigation.SubmenuItem
							as="a"
							id={ `main-nav__${ page.titleKey }` }
							key={ page.titleKey }
							label={ label }
							href={ page.pathname.pathname }
							target={ page.pathname.target || "_blank" }
						/>
					) : (
						<SidebarNavigation.SubmenuItem
							as={ NavLink }
							exact={ true }
							id={ `main-nav__${ page.titleKey }` }
							key={ page.titleKey }
							label={ label }
							to={ page.path }
						/>
					);
				} )
		}
		<SidebarNavigation.MenuItem
			icon={ UserIcon }
			id="submenuitem-sidebar-1"
			label="Account"
			defaultOpen={ true }
		>
			{
				props.menuRoutes
					.filter( page => page.showInAccountMenu )
					.map( page => {
						const title = props.intl.formatMessage( page.title );
						const label = (
							<span className={ styles.menuItem }>
								{ page.iconSource } { title }
								{ page.isExternal && page.externalLinkIconSource }
							</span>
						);

						return page.isExternal ? (
							<SidebarNavigation.SubmenuItem
								as="a"
								id={ `main-nav__${ page.titleKey }` }
								key={ page.titleKey }
								label={ label }
								href={ page.pathname.pathname }
								target={ page.pathname.target || "_blank" }
							/>
						) : (
							<SidebarNavigation.SubmenuItem
								as={ NavLink }
								exact={ true }
								id={ `main-nav__${ page.titleKey }` }
								key={ page.titleKey }
								label={ label }
								to={ page.path }
							/>
						);
					} )
			}
		</SidebarNavigation.MenuItem>
		<hr />
		<SidebarNavigation.MenuItem
			icon={ AiIconJSX }
			id="submenuitem-sidebar-1"
			label="Yoast AI Products"
			defaultOpen={ true }
		>
			{
				props.menuRoutes
					.filter( page => page.showInAiMenu )
					.map( page => {
						const title = props.intl.formatMessage( page.title );
						const label = (
							<span className={ styles.menuItem }>
								{ page.iconSource } { title }
								{ page.isExternal && page.externalLinkIconSource }
							</span>
						);

						return page.isExternal ? (
							// Override click to check if the customer should have access to Brand Insights.
							// eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
							<div key={ page.titleKey } onClick={ ( e ) => pressBrandInsightsMenuItem( e ) }>
								<SidebarNavigation.SubmenuItem
									as="a"
									id={ `main-nav__${ page.titleKey }` }
									label={ label }
									href={ page.pathname.pathname }
									target={ page.pathname.target || "_blank" }
								/>
							</div>
						) : (
							<SidebarNavigation.SubmenuItem
								as={ NavLink }
								exact={ true }
								id={ `main-nav__${ page.titleKey }` }
								key={ page.titleKey }
								label={ label }
								to={ page.path }
							/>
						);
					} )
			}
		</SidebarNavigation.MenuItem>
		<hr />
		<SidebarNavigation.MenuItem
			icon={ YoastPlatformIconJSX }
			id="submenuitem-sidebar-1"
			label="Yoast platforms"
			defaultOpen={ true }
		>
			{
				props.menuRoutes
					.filter( page => page.showInPlatformMenu )
					.map( page => {
						const title = props.intl.formatMessage( page.title );
						const label = (
							<span className={ styles.menuItem }>
								{ page.iconSource } { title }
								{ page.isExternal && page.externalLinkIconSource }
							</span>
						);

						return page.isExternal ? (
							<SidebarNavigation.SubmenuItem
								as="a"
								id={ `main-nav__${ page.titleKey }` }
								key={ page.titleKey }
								label={ label }
								href={ page.pathname.pathname }
								target={ page.pathname.target || "_blank" }
							/>
						) : (
							<SidebarNavigation.SubmenuItem
								as={ NavLink }
								exact={ true }
								id={ `main-nav__${ page.titleKey }` }
								key={ page.titleKey }
								label={ label }
								to={ page.path }
							/>
						);
					} )
			}
		</SidebarNavigation.MenuItem>
	</div>;
};

/**
 * The main menu.
 *
 * @param {Object} props The props to use.
 *
 * @returns {ReactElement} The rendered component.
 */
const MainMenu = ( props ) => {
	const [ showBrandInsightsNotice, setShowBrandInsightsNotice ] = useState( false );

	return (
		<div className={ styles.sideMenu }>
			<SidebarNavigation>
				<div className={ styles.mobileMenu }>
					<SidebarNavigation.Mobile
						openButtonId="button-open-settings-navigation-mobile"
						closeButtonId="button-close-settings-navigation-mobile"
						openButtonScreenReaderText="Open menu"
						closeButtonScreenReaderText="Close menu"
						aria-label="Menu"
					>
						<MenuItems
							menuRoutes={ props.menuRoutes }
							intl={ props.intl }
							setShowBrandInsightsNotice={ setShowBrandInsightsNotice }
						/>
					</SidebarNavigation.Mobile>
				</div>
				<div className={ styles.menuHeader }>
					<Logo context="sidebar" />
					<UserStatus />
				</div>
				<SidebarNavigation.Sidebar className={ styles.desktopMenu }>
					<MenuItems
						menuRoutes={ props.menuRoutes }
						intl={ props.intl }
						setShowBrandInsightsNotice={ setShowBrandInsightsNotice }
					/>
				</SidebarNavigation.Sidebar>
			</SidebarNavigation>
			<NeedBrandInsightsModal
				isOpen={ showBrandInsightsNotice }
				onClose={ () => setShowBrandInsightsNotice( false ) }
			/>
		</div>
	);
};

export default injectIntl( MainMenu );

MainMenu.propTypes = {
	menuRoutes: PropTypes.array.isRequired,
	intl: intlShape.isRequired,
};
